import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import ParentLayout from '../components/ParentLayout';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Settings, Eye, EyeOff, Users, Shield } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminVisibilityControls = () => {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  const pages = [
    { key: 'community', label: 'Community Feed', description: 'Social feed and announcements' },
    { key: 'volunteer', label: 'Volunteer Opportunities', description: 'Volunteer signup and tracking' },
    { key: 'fundraising', label: 'Fundraising', description: 'Fundraising campaigns and progress' },
    { key: 'messages', label: 'Messages', description: 'Team announcements and messages' },
    { key: 'schedule', label: 'Schedule', description: 'Events and practice schedule' }
  ];

  const roles = [
    { key: 'parent', label: 'Parents', icon: Users },
    { key: 'coach', label: 'Coaches', icon: Shield },
    { key: 'volunteer', label: 'Volunteers', icon: Users }
  ];

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/visibility-controls`, {
        withCredentials: true
      });
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch visibility settings:', error);
      // Set defaults if fetch fails
      setSettings({
        pages: {
          community: { parent: true, coach: true, volunteer: true },
          volunteer: { parent: true, coach: true, volunteer: true },
          fundraising: { parent: true, coach: true, volunteer: true },
          messages: { parent: true, coach: true, volunteer: false },
          schedule: { parent: true, coach: true, volunteer: true }
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleVisibility = (pageKey, roleKey) => {
    setSettings(prev => ({
      ...prev,
      pages: {
        ...prev.pages,
        [pageKey]: {
          ...prev.pages[pageKey],
          [roleKey]: !prev.pages[pageKey][roleKey]
        }
      }
    }));
  };

  const saveSettings = async () => {
    setSaving(true);
    setSaveMessage('');
    try {
      await axios.put(`${API_URL}/api/admin/visibility-controls`, settings, {
        withCredentials: true
      });
      setSaveMessage('Settings saved successfully!');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error) {
      setSaveMessage('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <ParentLayout>
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="animate-pulse space-y-4">
            <div className="h-10 bg-gray-200 rounded w-1/3"></div>
            <div className="h-64 bg-gray-200 rounded-lg"></div>
          </div>
        </div>
      </ParentLayout>
    );
  }

  return (
    <ParentLayout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="visibility-controls-page">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-navy mb-2 flex items-center" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Settings className="w-8 h-8 mr-3" />
            Visibility Controls
          </h1>
          <p className="text-gray-600">Control which pages and sections are visible to different user roles</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-navy">Page Visibility by Role</CardTitle>
            <CardDescription>
              Toggle switches to show or hide pages for each user role. Changes apply to all users in that role.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-2 font-semibold text-navy">Page / Section</th>
                    {roles.map(role => (
                      <th key={role.key} className="text-center py-3 px-4 font-semibold text-navy">
                        <div className="flex items-center justify-center space-x-1">
                          <role.icon className="w-4 h-4" />
                          <span>{role.label}</span>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pages.map(page => (
                    <tr key={page.key} className="border-b hover:bg-gray-50">
                      <td className="py-4 px-2">
                        <p className="font-medium text-navy">{page.label}</p>
                        <p className="text-sm text-gray-500">{page.description}</p>
                      </td>
                      {roles.map(role => (
                        <td key={role.key} className="text-center py-4 px-4">
                          <div className="flex items-center justify-center">
                            <button
                              onClick={() => toggleVisibility(page.key, role.key)}
                              className={`p-2 rounded-lg transition-colors ${
                                settings?.pages?.[page.key]?.[role.key]
                                  ? 'bg-green-100 text-green-600'
                                  : 'bg-gray-100 text-gray-400'
                              }`}
                              data-testid={`toggle-${page.key}-${role.key}`}
                            >
                              {settings?.pages?.[page.key]?.[role.key] ? (
                                <Eye className="w-5 h-5" />
                              ) : (
                                <EyeOff className="w-5 h-5" />
                              )}
                            </button>
                          </div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between mt-6 pt-4 border-t">
              {saveMessage && (
                <p className={`text-sm ${saveMessage.includes('Failed') ? 'text-red-600' : 'text-green-600'}`}>
                  {saveMessage}
                </p>
              )}
              <div className="ml-auto">
                <Button
                  onClick={saveSettings}
                  disabled={saving}
                  className="bg-navy hover:bg-navy/90"
                  data-testid="save-visibility-btn"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="text-navy text-lg">How Visibility Works</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-gray-600 space-y-2">
            <p><Eye className="w-4 h-4 inline text-green-600 mr-1" /> <strong>Visible:</strong> Users with this role can see and access this page.</p>
            <p><EyeOff className="w-4 h-4 inline text-gray-400 mr-1" /> <strong>Hidden:</strong> The page will not appear in navigation for this role.</p>
            <p className="text-gray-500 pt-2">Note: Admins always have full access to all pages regardless of these settings.</p>
          </CardContent>
        </Card>
      </div>
    </ParentLayout>
  );
};

export default AdminVisibilityControls;
